/**
 * 应用 Worker 单例（浏览器接线）：拉起 src/worker/index.ts + 鉴权态接入
 * globalStore。main.tsx 启动时调用一次；6d 的登录 actions 经 appWorker().call
 * 发起 RPC。node 测试环境不 import 本文件（会触碰 Worker/window）。
 */

import { createWorkerHost } from './workerHost';
import { connectAuth, type AppWorkerHost, type AuthConnection } from './authState';
import { connectConversations, type ConversationsConnection } from './conversationsState';
import { connectMessages, type MessagesConnection } from './messagesState';
import { connectConfig, type ConfigConnection } from './configState';
import { connectNetRoute, type NetRouteConnection } from './netRouteState';
import { connectDrafts, type DraftsConnection } from './draftsState';
import { connectArchive, type ArchiveConnection } from './archiveState';
import { connectStickers, type StickersConnection } from './stickersState';
import { connectContacts, type ContactsConnection } from './contactsState';
import { connectPresence, type PresenceConnection } from './presenceState';
import { connectUserProfile, type UserProfileConnection } from './userProfileState';
import { connectServerError } from './serverErrorState';
import { globalStore } from './global';
import { invariant } from '~/util/invariant';

let host: AppWorkerHost | undefined;
let conn: AuthConnection | undefined;
let convConn: ConversationsConnection | undefined;
let msgConn: MessagesConnection | undefined;
let cfgConn: ConfigConnection | undefined;
let netRouteConn: NetRouteConnection | undefined;
let draftConn: DraftsConnection | undefined;
let archiveConn: ArchiveConnection | undefined;
let stickerConn: StickersConnection | undefined;
let contactsConn: ContactsConnection | undefined;
let presenceConn: PresenceConnection | undefined;
let userProfileConn: UserProfileConnection | undefined;

export function appWorker(): AppWorkerHost {
  if (!host) {
    host = createWorkerHost({
      spawn: () => new Worker(new URL('../worker/index.ts', import.meta.url), { type: 'module' }),
      // 崩溃重建后 Worker 内存态丢失（事实源在 IndexedDB），重新拉快照对齐；
      // 死区内丢失的会话推送靠全量快照找平；时间线重开当前会话（重声明打开中）
      onRestart: () => {
        void conn?.sync();
        convConn?.resync();
        msgConn?.resync();
        cfgConn?.resync();
        netRouteConn?.resync();
        draftConn?.resync();
        archiveConn?.resync();
        stickerConn?.resync();
        contactsConn?.resync();
      },
    });
    conn = connectAuth(globalStore(), host);
    convConn = connectConversations(globalStore(), host);
    msgConn = connectMessages(globalStore(), host);
    cfgConn = connectConfig(globalStore(), host);
    // 不按 auth 门控：登录页也要显示并可切路线（netRouteState 文件头）
    netRouteConn = connectNetRoute(globalStore(), host);
    draftConn = connectDrafts(globalStore(), host);
    archiveConn = connectArchive(globalStore(), host);
    stickerConn = connectStickers(globalStore(), host);
    contactsConn = connectContacts(globalStore(), host);
    // presence 无快照可拉（事实源是推送流），不进 onRestart 的 resync 队列
    presenceConn = connectPresence(globalStore(), host);
    // 纯透传无本地态（Worker 侧缓存），崩溃重建无需 resync
    userProfileConn = connectUserProfile(globalStore(), host);
    // 无归属服务端错误（21f）：纯事件流，无快照可拉，不进 resync 队列
    connectServerError(globalStore(), host);
    // 推送订阅的绑定关系（21af）：登录/切换/冷启动对齐绑定，被动退出本地退订。
    // **整块懒加载**——这些动作没有一个发生在首绘之前，静态引会把整条订阅链
    // 拖进首屏 chunk（实测 +0.7KB，当场吃掉 80KB 预算的余量）。
    // 晚接也不会漏：connectPushLifecycle 建立时会读一次当前 auth，
    // 冷启动那一相照样能补上。失败就跳过——推送是旁路。
    const pushHost = host;
    void import('./pushLifecycle')
      .then((m) => m.connectPushLifecycle(globalStore(), pushHost))
      .catch(() => undefined);
  }
  return host;
}

/** 访问路线连接（14.39 路线设置页切换的入口）。确保 Worker 已拉起 */
export function netRouteConnection(): NetRouteConnection {
  appWorker();
  invariant(netRouteConn, 'netRouteConnection：appWorker 未初始化');
  return netRouteConn;
}

/** 会话列表连接（21ab 列表头「刷新」钮的入口）。确保 Worker 已拉起 */
export function conversationsConnection(): ConversationsConnection {
  appWorker();
  invariant(convConn, 'conversationsConnection：appWorker 未初始化');
  return convConn;
}

/** 应用配置连接（14.39 切路线后重拉 cdn 的入口）。确保 Worker 已拉起 */
export function configConnection(): ConfigConnection {
  appWorker();
  invariant(cfgConn, 'configConnection：appWorker 未初始化');
  return cfgConn;
}

/** 时间线连接（9e UI 触顶调 loadOlder 的入口）。确保 Worker 已拉起 */
export function messagesConnection(): MessagesConnection {
  appWorker();
  invariant(msgConn, 'messagesConnection：appWorker 未初始化');
  return msgConn;
}

/** 草稿连接（14.12 Composer 防抖保存的入口）。确保 Worker 已拉起 */
export function draftsConnection(): DraftsConnection {
  appWorker();
  invariant(draftConn, 'draftsConnection：appWorker 未初始化');
  return draftConn;
}

/** 归档连接（14.36 会话行菜单归档/取消归档的入口）。确保 Worker 已拉起 */
export function archiveConnection(): ArchiveConnection {
  appWorker();
  invariant(archiveConn, 'archiveConnection：appWorker 未初始化');
  return archiveConn;
}

/** 贴纸连接（14.16b 面板打开/发送上报的入口）。确保 Worker 已拉起 */
export function stickersConnection(): StickersConnection {
  appWorker();
  invariant(stickerConn, 'stickersConnection：appWorker 未初始化');
  return stickerConn;
}

/** 通讯录连接（14.18b 通讯录页加载的入口）。确保 Worker 已拉起 */
export function contactsConnection(): ContactsConnection {
  appWorker();
  invariant(contactsConn, 'contactsConnection：appWorker 未初始化');
  return contactsConn;
}

/** 在线状态连接（14.38b friend/info 真值回灌的入口）。确保 Worker 已拉起 */
export function presenceConnection(): PresenceConnection {
  appWorker();
  invariant(presenceConn, 'presenceConnection：appWorker 未初始化');
  return presenceConn;
}

/** 我的资料连接（14.29 我的 Tab/编辑资料页的入口）。确保 Worker 已拉起 */
export function userProfileConnection(): UserProfileConnection {
  appWorker();
  invariant(userProfileConn, 'userProfileConnection：appWorker 未初始化');
  return userProfileConn;
}
